import { Grid } from "@material-ui/core";
import { PublicEnquiryModel } from "../../domain/PublicEnquiry";
import { SectionProps } from "./SectionProps";
import { Controller } from "react-hook-form";
import { fieldLabels } from "./fieldLabels";
import { Error } from "../common/Error";
import { useLookup } from "@ofqual/portal-core/core/hooks";
import {
  aoService,
  categoryService,
  contactTypeService,
  directorateService,
  exceptionalCircumstanceService,
  focusEnquiryService,
  learnerTypeService,
  qualTypeService,
  themeEnquiryService,
} from "../../services";
import { Select } from "@ofqual/portal-components/components/Select";
import { DatePicker } from "@ofqual/portal-components/components/DatePicker";
import { MarkdownEditor } from "@ofqual/portal-components/components/MarkdownEditor";
import { SearchField } from "@ofqual/portal-components/components/SearchField";
import { useMemo } from "react";
import { QueryParams } from "@ofqual/portal-core/core/types";
import { useSelector } from "react-redux";
import { authSelector } from "../../redux/selectors";
import { FileInput } from "@ofqual/portal-components/components/FileInput";
import { AttachmentList } from "../common/AttachmentList";

export const EnquiryDetails: React.FC<{
  sectionProps: SectionProps<PublicEnquiryModel>;
}> = ({ sectionProps }) => {
  const {
    control,
    errors,
    disabled,
    isCreateMode,
    item,
    deleteAttachment,
    downloadAttachment,
  } = sectionProps;

  const [
    directorateLookups,
    focuseEnquiryLookups,
    categoryLookups,
    themeEquiryLookups,
    learnerTypeLookups,
    contactMethodLookups,
    exceptionalCircumstanceLookups,
  ] = useLookup(
    directorateService,
    focusEnquiryService,
    categoryService,
    themeEnquiryService,
    learnerTypeService,
    contactTypeService,
    exceptionalCircumstanceService
  );
  const { token } = useSelector(authSelector);

  const mappedOrgLookupService = useMemo(
    () => ({
      get: (id: string) => aoService.get(id.toString(), { token }),
      search: (params?: QueryParams) => {
        const queryParams: QueryParams = {
          ...params,
          search: params?.search || params?.query,
        };
        return aoService.search(queryParams, { token });
      }
    }),
    [token]
  );

  const [qualLookups] = useLookup({
    service: qualTypeService,
    id: "id",
    label: "description",
  });

  return (
    <Grid container spacing={3}>
      {!isCreateMode && (
        <>
          <Grid item xs={12}>
            <Controller
              control={control}
              name="organisation"
              as={
                <SearchField
                  label={fieldLabels.awardingOrg}
                  datatest={`pe-ao-field`}
                  searchFieldVariant="multiple"
                  service={mappedOrgLookupService}
                  readOnly={disabled}
                />
              }
            />
          </Grid>
          <Grid item xs={12}>
            <Controller
              control={control}
              name="qualificationType"
              as={
                <Select
                  selectVariant="multiple"
                  label={fieldLabels.qualification}
                  disabled={disabled}
                  datatest="qualType-field"
                >
                  {qualLookups}
                </Select>
              }
            />
          </Grid>
        </>
      )}
      {isCreateMode && (
        <Grid item xs={12} sm={6}>
          <Controller
            control={control}
            rules={{ required: true }}
            name="dateReceived"
            as={
              <DatePicker
                label={fieldLabels.dateReceived}
                dataTest="dateReceived-field"
                readOnly={disabled}
                required
                clearable
              />
            }
          />
          {errors.dateReceived?.type === "required" && (
            <Error data-test="dateReceived-validation">
              This field is required
            </Error>
          )}
        </Grid>
      )}
      <Grid item xs={12} sm={isCreateMode ? 6 : 12}>
        <Controller
          control={control}
          rules={{ required: true }}
          required
          name="directorate"
          as={
            <Select
              label={fieldLabels.directorate}
              data-test="directorate-field"
              readOnly={disabled}
              required
            >
              {directorateLookups}
            </Select>
          }
        />
        {errors.directorate?.type === "required" && (
          <Error data-test="directorate-validation">
            This field is required
          </Error>
        )}
      </Grid>
      <Grid item xs={12} sm={12}>
        <Controller
          control={control}
          rules={{ required: true, maxLength: 5000 }}
          name="summaryOfEnquiry"
          render={({ onChange, value }) => (
            <>
              <label>{fieldLabels.summaryEnquiry} *</label>
              <MarkdownEditor
                onChange={onChange}
                value={value}
                readOnly={disabled}
                dataTest="summary-field"
              />
            </>
          )}
        />
        {errors.summaryOfEnquiry?.type === "required" && (
          <Error data-test="summary-validation">This field is required</Error>
        )}
        {errors.summaryOfEnquiry?.type === "maxLength" && (
          <Error data-test="summary-length-validation">
            Max length exceeded
          </Error>
        )}
      </Grid>
      <Grid item xs={12} sm={4}>
        <Controller
          control={control}
          rules={{ required: true }}
          name="focusOfEnquiry"
          as={
            <Select
              label={fieldLabels.focusEnquiry}
              data-test="focusEnquiry-field"
              readOnly={disabled}
              required
            >
              {focuseEnquiryLookups}
            </Select>
          }
        />
        {errors.focusOfEnquiry?.type === "required" && (
          <Error data-test="focusOfEnquiry-validation">
            This field is required
          </Error>
        )}
      </Grid>
      <Grid item xs={12} sm={4}>
        <Controller
          control={control}
          rules={{ required: true }}
          name="themeOfEnquiry"
          as={
            <Select
              label={fieldLabels.themeEnquiry}
              data-test="themeOfEnquiry-field"
              readOnly={disabled}
              required
            >
              {themeEquiryLookups}
            </Select>
          }
        />
        {errors.themeOfEnquiry?.type === "required" && (
          <Error data-test="themeOfEnquiry-validation">
            This field is required
          </Error>
        )}
      </Grid>
      <Grid item xs={12} sm={4}>
        <Controller
          control={control}
          rules={{ required: true }}
          name="category"
          as={
            <Select
              label={fieldLabels.category}
              data-test="category-field"
              readOnly={disabled}
              required
            >
              {categoryLookups}
            </Select>
          }
        />
        {errors.category?.type === "required" && (
          <Error data-test="category-validation">This field is required</Error>
        )}
      </Grid>
      <Grid item xs={12} sm={4}>
        <Controller
          control={control}
          rules={{ required: true }}
          name="contactMethod"
          as={
            <Select
              label={fieldLabels.contactMethod}
              data-test="source-field"
              readOnly={disabled}
              required
            >
              {contactMethodLookups}
            </Select>
          }
        />
        {errors.contactMethod?.type === "required" && (
          <Error data-test="source-validation">This field is required</Error>
        )}
      </Grid>
      <Grid item xs={12} sm={4}>
        <Controller
          control={control}
          rules={{ required: true }}
          name="learnerType"
          as={
            <Select
              label={fieldLabels.learnerType}
              data-test="learnerType-field"
              readOnly={disabled}
              required
            >
              {learnerTypeLookups}
            </Select>
          }
        />
        {errors.learnerType?.type === "required" && (
          <Error data-test="learnerType-validation">
            This field is required
          </Error>
        )}
      </Grid>
      <Grid item xs={12} sm={4}>
        <Controller
          control={control}
          rules={{ required: true }}
          name="exceptionCircumstances"
          as={
            <Select
              label={fieldLabels.exceptionalCircumstance}
              data-test="exceptionCircumstance-field"
              readOnly={disabled}
              required
            >
              {exceptionalCircumstanceLookups}
            </Select>
          }
        />
        {errors.exceptionCircumstances?.type === "required" && (
          <Error data-test="exceptionCircumstances-validation">
            This field is required
          </Error>
        )}
      </Grid>
      <Grid item xs={12}>
        <Controller
          control={control}
          name="supportingFiles"
          defaultValue={[]}
          as={
            <FileInput
              multiple={true}
              dataTest="select-files-button"
              disabled={disabled}
            />
          }
        />
        {item?.supportingDocuments ? (
          <AttachmentList
            canEdit={!disabled}
            attachments={item.supportingDocuments}
            deleteAttachment={deleteAttachment}
            getBlob={downloadAttachment!}
          />
        ) : null}
      </Grid>
    </Grid>
  );
};
